import BookmarksAndCollectionsPillNav from "molecules/pill-navs/bookmark-and-collections-pill-nav";
import React from "react";
import Loader from "atoms/loaders/loader";
import { useLocalization } from "utilities/hooks/use-localization";
import PageNavigationMenu from "molecules/page-navigation/page-navigation-menu";
import Trans from "atoms/i18n/trans";
import useFilterBookmarks from "utilities/hooks/domain/my-link/use-filter-bookmarks";
import FilterByShareStatus from "./options/filter-by-share-status";
import UserBookmarkShareFilterOption from "models/enumerations/filtering/user-bookmarks/user-bookmark-share-filter-option";
import SortBy from "./options/sort-by";
import useSortBookmarks from "utilities/hooks/domain/my-link/use-sort-bookmarks";
import usePagedResultsFromParams from "utilities/hooks/use-paged-results-from-params";
import SearchBookmarks from "./options/search-bookmarks";
import useSearchBookmarks from "utilities/hooks/domain/my-link/use-search-bookmarks";
import MyBookmarksList from "./my-bookmarks-list";
import useAllBookmarksQuery from "utilities/hooks/queries/my-link/bookmarks/all-bookmarks/use-all-bookmarks-query";
import { BookmarkPaginationSettings } from "constants/bookmark-pagination-constants";
import MyBookmarksClearAll from "./options/my-bookmarks-clear-all";
import EmptyMyLinkPanel from "organisms/my-link/empty-mylink-panel";

export const CSS_CLASS_NAME = "c-my-bookmarks-view-by-all";
const DEFAULT_PAGE_SIZES = [10, 20, 50, 100];
const DEFAULT_TAKE_SIZE = BookmarkPaginationSettings.DefaultViewAllTake;

const MyBookmarksViewByAll = () => {
    const { t } = useLocalization();
    const {
        currentPage,
        numberOfPages,
        onPageLast,
        onPageNext,
        onPageSizeChange,
        onSelectPage,
        setRowCount: setTotalResultsForPagination,
        take,
        skip,
        rowCount,
    } = usePagedResultsFromParams(DEFAULT_TAKE_SIZE);

    const { shareFilter, onShareFilterChange } = useFilterBookmarks();
    const { sortBy, onSortByChange } = useSortBookmarks();
    const { searchText, onSearchTextChange } = useSearchBookmarks();

    const handleOnSuccess = (newValue) => {
        setTotalResultsForPagination(newValue.bookmarksLength);
    };

    const { bookmarks, isLoading } = useAllBookmarksQuery({
        searchText,
        skip,
        take,
        sortBy,
        onSuccess: handleOnSuccess,
        currentUserOnly:
            shareFilter === UserBookmarkShareFilterOption.PrivateOnly,
    });

    // Accounts for instances where row count is less than the results per page to avoid "Showing 20 of 2 bookmarks"
    const currentCountInView = Math.min(take, rowCount - skip);

    return (
        <section className={CSS_CLASS_NAME}>
            <div className={`${CSS_CLASS_NAME}__navigation`}>
                <BookmarksAndCollectionsPillNav />
                <div className={`${CSS_CLASS_NAME}__dropdowns`}>
                    <FilterByShareStatus
                        shareStatus={shareFilter}
                        onChange={onShareFilterChange}
                    />
                    <SortBy sort={sortBy} onChange={onSortByChange} />
                    <SearchBookmarks
                        searchText={searchText}
                        onChange={onSearchTextChange}
                    />
                </div>
            </div>

            <div className={`${CSS_CLASS_NAME}__title`}>
                {isLoading ? (
                    "Loading Bookmarks"
                ) : (
                    <Trans i18nKey="mylink-view-all-title">
                        Showing {{ take: currentCountInView }} of{" "}
                        {{ total: bookmarks?.bookmarksLength }} Bookmarks
                    </Trans>
                )}
                <MyBookmarksClearAll />
            </div>
            {isLoading && (
                <Loader
                    accessibleText={t("loadingItem", {
                        item: t("bookmark_plural"),
                    })}
                />
            )}
            {bookmarks &&
                !isLoading &&
                (bookmarks.bookmarks.length > 0 ? (
                    <MyBookmarksList bookmarks={bookmarks.bookmarks} />
                ) : (
                    <EmptyMyLinkPanel />
                ))}
            <div className={`${CSS_CLASS_NAME}__footer`}>
                <PageNavigationMenu
                    currentPage={currentPage}
                    numberOfPages={numberOfPages}
                    onNavigateBack={onPageLast}
                    onNavigateForward={onPageNext}
                    onPageSizeChange={onPageSizeChange}
                    onSelectPage={onSelectPage}
                    pageSize={take}
                    pageSizes={DEFAULT_PAGE_SIZES}
                    resultTotal={bookmarks?.bookmarksLength!}
                />
            </div>
        </section>
    );
};

export default MyBookmarksViewByAll;
